import { Box, Typography } from "@mui/material";
import { fontLink, isArray } from "../../base";
import { useResult } from "../../Providers/Result/ResultDataProvider";
import { colorRed } from "../../Resources/Design/theme";

export const tagHighlight = {
    color: '#FBC42D',
    text_color: '#011949',
    nodark: true,
}

export const tagRed = {
    color: "#FA5C55",
    text_color: "#FFFFFF",
}

export const tagWarn = {
    ...tagHighlight,
    warn: true,
}

export const joinValues = (...values) =>
    values.filter(v => v != null && v !== '').join(' ');

export function isValidText(str) {
    if (!str) return false;
    // Contains only / , ; . or spaces
    return !(/^[\/;,. ]+$/.test(str));
}

export const resultAnyValid = (...values) => {
    for (const value of values) {
        if (isValidText(value)) {
            return true;
        }
    }
    return false;
}

export const CzechFlagIcon = <>
    <Box sx={{ height: '20px', width: '20px' }}>
        <Box component={"img"} alt="" src={`https://flagcdn.com/w40/cz.png`}
            onError={(e) => (e.target.style.display = 'none')}
            sx={{
                height: '20px', width: '20px',
                borderRadius: '50%'
            }} />
    </Box>

    <Typography sx={{
        fontFamily: 'inter', fontWeight: 500, ...fontLink, mx: '10px',
        color: '#FBFBFB', textAlign: 'left'
    }}>Česko</Typography>
</>;

export const getFlagIcon = (code = "cz", text = "Česko", sx = {}) => {
    return <>
        <Box sx={{ height: '20px', width: '20px' }}>
            <Box component={"img"} alt="" src={`https://flagcdn.com/w40/${code}.png`}
                onError={(e) => (e.target.style.display = 'none')}
                sx={{
                    height: '20px', width: '20px',
                    borderRadius: '50%', ...sx
                }} />
        </Box>

        <Typography sx={{
            fontFamily: 'inter', fontWeight: 500, ...fontLink, mx: '10px',
            color: '#FBFBFB', textAlign: 'left'
        }}>{text}</Typography>
    </>
};

export const getExpandClick = (px = 5) => {
    return {
        position: 'relative', '&::before': { content: '""', position: 'absolute', top: -px, left: -px, right: -px, bottom: -px }
    }
}

function createValidatedDate(y, m, d) {
    const date = new Date(y, m - 1, d);

    if (
        date.getFullYear() !== y ||
        date.getMonth() !== m - 1 ||
        date.getDate() !== d
    ) {
        return null;
    }

    return date;
}

// "2013-01-01"
export const getDateFromDashVisual = (date) => {
    try {
        if (!date) return '';

        const parts = String(date).split("-");
        if (parts.length !== 3) return '';

        const y = Number(parts[0]);
        const m = Number(parts[1]);
        const d = Number(parts[2]);

        const valid = createValidatedDate(y, m, d);
        if (!valid) return '';

        return `${String(d).padStart(2, "0")}.${String(m).padStart(2, "0")}.${y}`;
    } catch {
        return '';
    }
};

// 2006-03-23
export const getDateFromDash = (date) => {
    if (!date) return null;

    const parts = String(date).split("-");
    if (parts.length !== 3) return null;

    const y = Number(parts[0]);
    const m = Number(parts[1]);
    const d = Number(parts[2]);

    if (y < 1900) return null;

    return createValidatedDate(y, m, d);
};

// 23-03-2026
export const getDateFromDashReverse = (date) => {
    if (!date) return null;

    const parts = String(date).split("-");
    if (parts.length !== 3) return null;

    const d = Number(parts[0]);
    const m = Number(parts[1]);
    const y = Number(parts[2]);

    if (y < 1900) return null;

    return createValidatedDate(y, m, d);
};

export function isValidDate(date) {
    return date && Object.prototype.toString.call(date) === "[object Date]" && !isNaN(date);
}

export function getDateFromDashSafe(str) {
    if (!str) return null;

    if (isValidDate(str)) return str;

    // Normalize all common separators to "-"
    const normalized = String(str)
        .trim()
        .replace(/[./\s]+/g, "-");

    const parts = normalized.split("-");
    if (parts.length !== 3) return null;

    let y, m, d;

    // YYYY-MM-DD
    if (parts[0].length === 4) {
        [y, m, d] = parts.map(Number);
    }
    // DD-MM-YYYY
    else {
        [d, m, y] = parts.map(Number);
    }

    if (!y || y < 1900) return null;

    return createValidatedDate(y, m, d);
}

export const getDateFromTDashVisual = (dateString) => {
    try {
        if (!dateString || typeof dateString !== "string") {
            return false;
        }

        const date = new Date(dateString);

        if (isNaN(date.getTime())) {
            return false;
        }

        const day = String(date.getDate()).padStart(2, '0');
        const month = String(date.getMonth() + 1).padStart(2, '0'); // Months are 0-based
        const year = date.getFullYear();

        return `${day}.${month}.${year}`;
    } catch (e) { return false; }
}

export const getDateFromTDash = (dateString) => {
    try {
        if (!dateString || typeof dateString !== "string") {
            return false;
        }

        const date = new Date(dateString);

        if (isNaN(date.getTime())) {
            return false;
        }

        return date;
    } catch (e) { return false; }
}

export const getDateVisual = (date) => {
    if (!date || isNaN(date?.getTime() || null)) return null;
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();

    return `${day}.${month}.${year}`;
}

export const numberWithSpaces = (num) => {
    try {
        return num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
    } catch (e) {
        return `-`;
    }
}

export const yearMillis = 31536000000;

export const Flag = ({ code, sx = {} }) => (
    <Box
        component="img"
        src={`https://flagcdn.com/w40/${code}.png`}
        srcSet={`https://flagcdn.com/w80/${code}.png 2x`}
        onError={(e) => (e.target.style.display = 'none')}
        alt={code}
        sx={{
            width: 24,
            height: 24,
            borderRadius: "50%",
            objectFit: "cover",
            border: '1px solid #eee',
            display: 'block',
            ...sx,
        }}
    />
);

export const useHeaderTags = ({
    excluded = [],
} = {}) => {
    const { data, vin, znacka, model, manufactured, paliva } = useResult() || {};
    const { base = {} } = data || {};
    const { VozidloKaroserieBarva } = base || {};

    const tags = [];
    if (isValidText(znacka) || isValidText(model)) {
        tags.push(joinValues(znacka ?? "", model ?? ""));
    }

    if (isArray(paliva)) {
        tags.push(...paliva);
    }

    if (VozidloKaroserieBarva && !excluded.includes("color")) {
        tags.push(`Barva: ${VozidloKaroserieBarva}`);
    }
    if (manufactured && manufactured?.getFullYear()) {
        tags.push(`Rok výroby: ${manufactured?.getFullYear()}`);
    }
    if (vin && !excluded.includes("vin")) {
        tags.push(`VIN: ${vin}`);
    }

    return tags;
}

function normalize(str) {
    return str
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .replace(/\s+/g, '-')
        .toLowerCase()
        .replace(/[^a-zA-Z0-9-]/g, '');
}

export function getCarLogoPath(brandName, basePath = '/data/car_logos/') {
    if (!brandName) return null;

    return `${basePath}${normalize(brandName)}.png`;
}

